import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Button,
  Container,
  Row,
  Table as BTable,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableCell } from "@/components/Elements/TableCell";
import { matchApi } from "@/api";
import { TASK_TYPES } from "@/constants/taskTypes";

const Tasks = () => {
  const navigate = useNavigate();
  const { user } = useSelector(({ auth }) => auth);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const columns = [
    {
      header: "Id",
      cell: (info) => <>{info.row.index}</>,
    },
    {
      accessorKey: "task_type",
      header: "Task Type",
    },
    {
      accessorKey: "match",
      header: "Match Id",
    },
    {
      accessorKey: "id",
      header: "Task Id",
    },
    {
      accessorKey: "last_taken",
      header: "Last Taken",
      cell: (info) => {
        const date = new Date(info.getValue());

        // Formatting options
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        };

        // Convert to human-readable format using user's local timezone
        const formattedDate = date.toLocaleDateString("en-US", options);
        return <>{formattedDate}</>;
      },
    },
    {
      id: "Actions",
      header: "Actions",
      cell: (info) => {
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => gotoTask(info.row.original)}
          >
            <FontAwesomeIcon icon={["fas", "hand-point-right"]} size="sm" /> Go
            To Task
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: tasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const gotoTask = (task) => {
    const { task_type, match: matchId, id: taskId, task: taskDetails } = task;
    let supertrackId;

    switch (task_type) {
      case TASK_TYPES.ball_supertrack:
        if (taskDetails?.super_track_uid === null) {
          toast.warning("There's no supertrack id in task data.");
          break;
        }
        const { frame_start, frame_end, super_track_uid } = taskDetails;
        navigate(
          `/panel/markup/super-tracks/${matchId}/markup/${super_track_uid}?taskId=${taskId}&type=ball&frame_start=${frame_start}&frame_end=${frame_end}`
        );
        break;
      case TASK_TYPES.supertrack:
        if (
          taskDetails?.super_track_uid !== null &&
          taskDetails?.super_track_uid !== undefined
        ) {
          supertrackId = taskDetails.super_track_uid;
          navigate(
            `/panel/markup/super-tracks/${matchId}/markup/${supertrackId}?taskId=${taskId}`
          );
        } else {
          toast.warning("There's no supertrack id in task data.");
        }
        break;
      case TASK_TYPES.track:
        navigate(`/panel/markup/track/${matchId}?taskId=${taskId}`);
        break;
      case TASK_TYPES.event:
        navigate(`/panel/events-resolving/${matchId}?taskId=${taskId}`);
        break;
      case TASK_TYPES.ball_touch:
        navigate(`/panel/ball-touch/${matchId}?taskId=${taskId}`);
        break;
      case TASK_TYPES.match_event:
        navigate(`/panel/event-light/${matchId}?taskId=${taskId}`);
        break;
      case TASK_TYPES.validate:
        const { frame_start_id, frame_stop_id } = taskDetails;
        navigate(
          `/panel/events-validation/${matchId}?taskId=${taskId}&frame_start_id=${frame_start_id}&frame_stop_id=${frame_stop_id}`
        );
        break;
      case TASK_TYPES.event_tagging:
        navigate(`/panel/events-tagging/${matchId}?taskId=${taskId}`);
        break;
      default:
        toast.warning("Unknown Task Type.");
        break;
    }
  };

  useEffect(() => {
    if (!user) return;

    async function fetchCurrentTasks() {
      try {
        setIsLoading(true);
        const currentTasks = await matchApi.getCurrentTasks(user.id);
        if (!currentTasks) return;
        const filteredTasks = currentTasks.filter(
          (task) =>
            ![
              TASK_TYPES.ball_touch,
              TASK_TYPES.validate,
              TASK_TYPES.track,
              TASK_TYPES.event,
            ].includes(task.task_type)
        );

        setTasks(filteredTasks);
      } catch (err) {
        toast("Something went wrong while fetching current tasks.");
      } finally {
        setIsLoading(false);
      }
    }
    fetchCurrentTasks();
  }, [user]);
  return (
    <Container className="py-3" fluid>
      <Row className="d-flex justify-content-center">
        <h2 className="text-center tw-font-medium tw-tracking-wider">
          My Tasks
        </h2>
        {tasks.length ? (
          <BTable responsive hover size="md">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </BTable>
        ) : (
          <div className="m-3 d-flex justify-content-center">
            {isLoading ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "There are no tasks that you are working on."
            )}
          </div>
        )}
      </Row>
    </Container>
  );
};

export default Tasks;
